import { useState, useEffect } from 'react';
import { EyeOff } from 'lucide-react';
import { palette, shadow, transition, admin } from '../../styles/tokens';
import { useAuthStore } from '../../stores/authStore';
import { useIndustryLens } from '../../hooks/useIndustryLens';
import { useVehicles, useTrips, useMaintenance } from './hooks';
import { VehiclesTab } from './VehiclesTab';
import { TripsTab } from './TripsTab';
import { MaintenanceTab } from './MaintenanceTab';
import { Skeleton, TableSkeleton } from '../../components/common/Skeleton';
import { Toast } from '../../components/common/Toast';
import { FLEET_TABS, type FleetTab } from './config';

export function FleetPage() {
  const business = useAuthStore(s => s.business);
  const user = useAuthStore(s => s.user);
  const { features, isLensActive, industryName } = useIndustryLens();
  const [tab, setTab] = useState<FleetTab>('vehicles');
  const [toast, setToast] = useState<{ msg: string; type: 'success' | 'error' } | null>(null);

  const { vehicles, loading: vLoading, error: vError, createVehicle, updateVehicle, retireVehicle } = useVehicles(business?.id);
  const { trips, loading: tLoading, hasMore, loadMore, summary: tripSummary, vehicleFilter: tripVF, setVehicleFilter: setTripVF } = useTrips(business?.id);
  const { records: maintRecords, loading: mLoading, vehicleFilter: maintVF, setVehicleFilter: setMaintVF, createMaintenance, completeMaintenance } = useMaintenance(business?.id);

  useEffect(() => { document.title = 'CrewBit · Fleet'; }, []);

  if (isLensActive && !features.showFleet) {
    return (
      <div>
        <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px', marginBottom: 24 }}>Fleet</div>
        <div style={{ padding: '60px 20px', textAlign: 'center', background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}` }}>
          <EyeOff size={24} color={palette.g400} style={{ marginBottom: 12 }} />
          <div style={{ fontSize: 15, fontWeight: 600, color: palette.g700, marginBottom: 6 }}>Fleet is not typically used by {industryName} businesses</div>
          <div style={{ fontSize: 13.5, color: palette.g500 }}>Switch your industry lens to see this module, or exit the lens to return to your view.</div>
        </div>
      </div>
    );
  }

  if (vLoading && tab === 'vehicles') {
    return (
      <div>
        <Skeleton width={100} height={24} style={{ marginBottom: 24 }} />
        <div style={{ background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, padding: 20 }}><TableSkeleton rows={5} columns={6} /></div>
      </div>
    );
  }

  if (vError) {
    return (
      <div>
        <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px', marginBottom: 24 }}>Fleet</div>
        <div style={{ padding: '40px 0', textAlign: 'center', color: palette.red, fontSize: 13.5 }}>{vError}</div>
      </div>
    );
  }

  return (
    <div>
      {/* Header */}
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 24, fontWeight: 700, color: admin.header, letterSpacing: '-0.5px' }}>Fleet</div>
        <div style={{ fontSize: 13.5, color: admin.caption, marginTop: 4 }}>Vehicles, trips, and maintenance</div>
      </div>

      {/* Tabs */}
      <div role="tablist" aria-label="Fleet sections" style={{ display: 'flex', gap: 0, background: palette.g100, borderRadius: 8, padding: 2, marginBottom: 16, width: 'fit-content' }}>
        {FLEET_TABS.map(t => {
          const active = tab === t.key;
          return (
            <button
              key={t.key}
              role="tab"
              id={`fleet-tab-${t.key}`}
              aria-selected={active}
              aria-controls={`fleet-tabpanel-${t.key}`}
              tabIndex={active ? 0 : -1}
              onClick={() => setTab(t.key)}
              style={{
                padding: '6px 16px', borderRadius: 5, border: 'none', cursor: 'pointer',
                fontSize: 12.5, fontWeight: 500, fontFamily: 'inherit',
                background: active ? palette.g800 : 'transparent',
                color: active ? palette.white : palette.g500,
                transition: `all ${transition.fast}`,
              }}
            >{t.label}</button>
          );
        })}
      </div>

      {/* Content */}
      <div
        role="tabpanel"
        id={`fleet-tabpanel-${tab}`}
        aria-labelledby={`fleet-tab-${tab}`}
        style={{ background: palette.white, borderRadius: admin.cardRadius, border: `1px solid ${admin.border}`, padding: 20, boxShadow: shadow.card }}
      >
        {tab === 'vehicles' && (
          <VehiclesTab vehicles={vehicles} userId={user?.id ?? ''}
            onCreate={async (v, uid) => { const ok = await createVehicle(v, uid); if (ok) setToast({ msg: 'Vehicle added', type: 'success' }); return ok; }}
            onUpdate={async (id, v, uid) => { const ok = await updateVehicle(id, v, uid); if (ok) setToast({ msg: 'Vehicle updated', type: 'success' }); return ok; }}
            onRetire={async (id, name, uid) => { const ok = await retireVehicle(id, name, uid); if (ok) setToast({ msg: 'Vehicle retired', type: 'success' }); return ok; }}
          />
        )}
        {tab === 'trips' && (
          <TripsTab trips={trips} loading={tLoading} hasMore={hasMore} loadMore={loadMore}
            summary={tripSummary} vehicles={vehicles} vehicleFilter={tripVF} setVehicleFilter={setTripVF} />
        )}
        {tab === 'maintenance' && (
          <MaintenanceTab records={maintRecords} loading={mLoading} vehicles={vehicles}
            vehicleFilter={maintVF} setVehicleFilter={setMaintVF}
            onCreate={async (input, uid) => { const ok = await createMaintenance(input, uid); if (ok) setToast({ msg: 'Maintenance scheduled', type: 'success' }); return ok; }}
            onComplete={async (id, cost, uid) => { const ok = await completeMaintenance(id, cost, uid); if (ok) setToast({ msg: 'Maintenance completed', type: 'success' }); return ok; }}
            userId={user?.id ?? ''} />
        )}
      </div>

      {toast && <Toast message={toast.msg} type={toast.type} isVisible={!!toast} onDismiss={() => setToast(null)} />}
    </div>
  );
}
